// Hideo - closes a small popup (menu, panel) on a click outside it and, optionally, on Esc. The document listeners are
// installed only while the popup is open and removed when it closes or the component goes away.
// Used by CalJump.vue, VerdictButton.vue, SoundButton.vue and TopControls.vue (the project menu).
import { onBeforeUnmount, watch, type Ref } from 'vue'

export interface PopoverOpts {
  /** the event that counts as "outside" (default click) */
  event?: 'click' | 'pointerdown'
  /** listen in the capture phase, before the page's own handlers (default false) */
  capture?: boolean
  /** an outside click only closes the popup: it is stopped and its default prevented (needs capture) */
  swallow?: boolean
  /** Esc closes the popup too (and goes no further) */
  esc?: boolean
}

export function usePopover(open: Ref<boolean>, root: Ref<HTMLElement | null>, opts: PopoverOpts = {}): void {
  const event = opts.event || 'click', capture = !!opts.capture
  function outside(e: Event): void {
    if (!open.value || !root.value || root.value.contains(e.target as Node)) return
    if (opts.swallow) { e.stopPropagation(); e.preventDefault() }
    open.value = false
  }
  function key(e: KeyboardEvent): void {
    if (e.key === 'Escape') { e.stopPropagation(); open.value = false }
  }
  function off(): void {
    document.removeEventListener(event, outside, capture)
    if (opts.esc) document.removeEventListener('keydown', key, true)
  }
  watch(open, o => {
    off()
    if (!o) return
    document.addEventListener(event, outside, capture)
    if (opts.esc) document.addEventListener('keydown', key, true)
  }, { immediate: true })
  onBeforeUnmount(off)
}
